import { CheckoutContext } from "@/components/CheckoutForm/utils";
import { Button, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { PaymentElement } from "@stripe/react-stripe-js";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface CheckoutPaymentFormProps {
  ctx: CheckoutContext;
  nextIsPending: boolean;
  onNext: () => void;
  onPrev: () => void;
}

export const CheckoutPaymentForm = ({
  nextIsPending,
  onNext,
  onPrev,
}: CheckoutPaymentFormProps) => {
  const [isPaymentValid, setIsPaymentValid] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [validationError, setValidationError] = useState("");
  const paymentElementRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => {
    if (!showTooltip) return;
    const timer = setTimeout(() => setShowTooltip(false), 3000);
    return () => clearTimeout(timer);
  }, [showTooltip]);

  return (
    <form
      className="flex h-full flex-col gap-6 pt-4 md:pt-1"
      noValidate
      onSubmit={e => {
        e.preventDefault();
        if (!isPaymentValid) {
          setValidationError("Please complete your payment information.");
          setShowTooltip(true);
          paymentElementRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return;
        }
        onNext();
      }}
    >
      <div className="flex h-full flex-col gap-6 px-4">
        <div className="flex flex-col gap-3">
          <p className="mb-1 text-[18px] font-bold leading-none">
            Payment Method
          </p>
          <div className="payment-element-container" ref={paymentElementRef}>
            <PaymentElement
              options={{
                layout: {
                  type: "tabs",
                },
                fields: {
                  billingDetails: {
                    name: "never",
                    email: "never",
                    phone: "never",
                    address: "never",
                  },
                },
              }}
              onChange={e => {
                setIsPaymentValid(e.complete);
              }}
            />
          </div>
          {/* Saved-cards guidance (2026-08-26, Michael-approved): the Card
              tab + save checkbox IS the add-a-new-card path, and card
              numbers can't be edited in place anywhere in Stripe — a
              replacement card is added new and the old one removed. Make
              both pathways discoverable right where the tiles render. */}
          <p className="text-xs text-[#636363]">
            Paying with a new card? Choose the <b>Card</b> tab and tick the
            save box to keep it on your account for next time. To remove a
            card or update its billing details, manage your{" "}
            <Link
              to="/profile/payment-methods"
              // ProfileLayout + ProfileNavbar honor state.from for their
              // Return buttons (and persist it in sessionStorage) — without
              // it the profile's Return falls back to /visualizer and the
              // customer loses the way back to checkout (Michael, 2026-08-26).
              state={{ from: "/checkout" }}
              className="underline"
              style={{ color: "#489B9B" }}
            >
              saved cards
            </Link>{" "}
            — your cart will be here when you get back.
          </p>
        </div>
      </div>
      <div className="sticky bottom-0 z-20 grid grid-cols-2 justify-between gap-3 rounded-b-xl border-t border-[#00000014] bg-white px-4 py-3 md:flex">
        <Button
          type="button"
          size="md"
          radius="xl"
          onClick={onPrev}
          w={isMobile ? "100%" : "120px"}
          h="40px"
          style={{ color: "#636363", backgroundColor: "#00000014" }}
        >
          Previous
        </Button>
        <Tooltip
          label={validationError}
          opened={showTooltip}
          position="top-end"
          withArrow
          arrowPosition="center"
          color="dark"
          multiline
          transitionProps={{ transition: "pop", duration: 300 }}
        >
          <Button
            type="submit"
            size="md"
            radius="xl"
            loading={nextIsPending}
            disabled={nextIsPending}
            w={isMobile ? "100%" : "200px"}
            h="40px"
          >
            Save & Continue
          </Button>
        </Tooltip>
      </div>
    </form>
  );
};
